import React, { useCallback, useEffect } from "react";

import PeopleIcon from "@mui/icons-material/PeopleOutlined";

import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { FilterContext, useFilter } from "../../components/Filter";
import {
    ListView,
    MainPage,
    MainView,
    PageContext,
    usePageState,
} from "../../components/PageLayout";
import { initPeriod } from "../../components/Period";
import { useSideSheet } from "../../components/SideSheet";
import { useUserInfo } from "../../utils/security";
import { fetchEmployeeDetail, fetchEmployeeList, fetchEmployeeOverview } from "./mock";
import { EmployeesList } from "./EmployeesList";
import { EmployeesOverview } from "./EmployeesOverview";
import { EmployeesDetail } from "./EmployeesDetail";

export const EmployeesIcon = PeopleIcon;

export const EmployeesPage = ({ pagePath }) => {
    const navigate = useNavigate();
    const { id: detailId } = useParams();
    const sideSheet = useSideSheet();
    const { userInfo } = useUserInfo();

    const filterState = useFilter({
        filter: {
            period: initPeriod("year"),
        },
    });
    const { filterValue } = filterState;

    const pageState = usePageState();
    const { state, setListLoading, setList, setDataLoading, setData } = pageState;

    //TODO - implement functions TODO
    const addEmployee = useCallback(
        ({ id, name, ...rest }) => {
            sideSheet.show({
                employeeTypeId: id,
                employeeTypeName: name,
                date: dayjs(),
                fiscalYear: dayjs().year(),
                dueDate:
                    rest.hasDueDate && rest.defaultDueDateDays
                        ? dayjs().add(rest.defaultDueDateDays || 0, "days")
                        : undefined,
                currencyCode: userInfo?.defaultCurrency?.code || "CZK",
                ...(rest.hasSubjectInfo
                    ? {
                          subjectName: userInfo?.financesSettings?.name,
                          subjectAddress: userInfo?.financesSettings?.address,
                          subjectIdNumber: userInfo?.financesSettings?.idNumber,
                          subjectTaxIdNumber: userInfo?.financesSettings?.taxIdNumber,
                      }
                    : {}),
                ...rest,
            });
        },
        [sideSheet, userInfo]
    );

    useEffect(() => {
        const fetchList = async (filter) => {
            setListLoading(true);
            try {
                const data = await fetchEmployeeList(filter);
                setList(data);
            } catch (e) {
                console.error(e);
                setList({ error: true });
            } finally {
                setListLoading(false);
            }
        };

        const fetchOverview = async (filter) => {
            setDataLoading(true);
            try {
                const data = await fetchEmployeeOverview(filter);
                setData(data);
            } catch (e) {
                console.error(e);
                setList({ error: true });
            } finally {
                setDataLoading(false);
            }
        };

        fetchList(filterValue);
        fetchOverview(filterValue);
    }, [filterValue]);

    //fetch loaded data
    useEffect(() => {
        const fetchDetail = async (id) => {
            console.log("fetching detail", id);
            sideSheet.show({}, true);

            try {
                const data = await fetchEmployeeDetail(id);
                sideSheet.show(data, false);
            } catch (e) {
                console.error(e);
                sideSheet.show({ error: true }, false);
            }
        };
        if (!isEmpty(detailId)) {
            fetchDetail(detailId);
        }
    }, [detailId, sideSheet.show, sideSheet.hide, fetchEmployeeDetail]);

    return (
        <>
            <FilterContext.Provider value={filterState}>
                <PageContext.Provider value={pageState}>
                    <MainPage>
                        <ListView>
                            <EmployeesList
                                onAddEmployee={addEmployee}
                                selectItem={({ id }) => navigate(`/${pagePath}/${id}`)}
                                loading={state.listLoading}
                                data={state.list}
                                detailId={detailId}
                            />
                        </ListView>
                        <MainView>
                            {isEmpty(detailId) ? (
                                <EmployeesOverview />
                            ) : (
                                <EmployeesDetail
                                    employeeId={detailId}
                                    onBack={() => navigate(`/${pagePath}`)}
                                />
                            )}
                        </MainView>
                    </MainPage>
                </PageContext.Provider>
            </FilterContext.Provider>
        </>
    );
};
